import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import {
  AddGoalSelect,
  AVAILABLE_METRICS,
  CopyToFYSelect,
  type GoalRow,
  GoalRowInput,
  getMetricDef,
  SAVED_BORDER,
  SectionHeader,
  useSaveFlash,
} from "./shared";

interface SourceGoalSet {
  id: string;
  name: string;
  sources: string[];
  targets: { metric: string; targetValue: number }[];
}

export function SourceGoalsSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [sets, setSets] = useState<SourceGoalSet[]>([]);
  const [allSources, setAllSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/source-goal-sets?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        setSets(data.sourceGoalSets);
        onDataChange(data.sourceGoalSets.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  const fetchSources = useCallback(async () => {
    const res = await fetch("/api/deal-values/sources");
    if (res.ok) {
      const data = await res.json();
      setAllSources(data.values ?? []);
    }
  }, []);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  async function addSet() {
    const res = await fetch(`/api/owners/${ownerId}/source-goal-sets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, name: "New Source Set", sources: [] }),
    });
    if (res.ok) {
      const data = await res.json();
      setSets((prev) => {
        const updated = [...prev, data.sourceGoalSet];
        onDataChange(true);
        return updated;
      });
    }
  }

  async function removeAll() {
    await Promise.all(
      sets.map((set) =>
        fetch(`/api/owners/${ownerId}/source-goal-sets/${set.id}`, { method: "DELETE" }),
      ),
    );
    setSets([]);
    onDataChange(false);
    onRemove?.();
  }

  async function deleteSet(setId: string) {
    await fetch(`/api/owners/${ownerId}/source-goal-sets/${setId}`, { method: "DELETE" });
    setSets((prev) => {
      const updated = prev.filter((s) => s.id !== setId);
      onDataChange(updated.length > 0);
      return updated;
    });
  }

  function updateSetLocal(setId: string, updates: Partial<SourceGoalSet>) {
    setSets((prev) => prev.map((s) => (s.id === setId ? { ...s, ...updates } : s)));
  }

  async function copyToFY(targetFY: number) {
    if (sets.length === 0) return;
    for (const set of sets) {
      const createRes = await fetch(`/api/owners/${ownerId}/source-goal-sets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear: targetFY, name: set.name, sources: set.sources }),
      });
      if (createRes.ok) {
        const created = await createRes.json();
        const targetsToSet = set.targets
          .filter((t) => t.targetValue != null)
          .map((t) => ({ metric: t.metric, targetValue: t.targetValue }));
        if (targetsToSet.length > 0) {
          await fetch(
            `/api/owners/${ownerId}/source-goal-sets/${created.sourceGoalSet.id}/targets`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ targets: targetsToSet }),
            },
          );
        }
      }
    }
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Source Goals" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && sets.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          {sets.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-2">
              No source goal sets for {formatFY(fiscalYear)}.{!readOnly && " Add one below."}
            </p>
          ) : (
            <div className="space-y-6">
              {sets.map((set) => (
                <SourceGoalSetCard
                  key={set.id}
                  ownerId={ownerId}
                  set={set}
                  allSources={allSources}
                  onDelete={() => deleteSet(set.id)}
                  onUpdate={(updates) => updateSetLocal(set.id, updates)}
                  readOnly={readOnly}
                />
              ))}
            </div>
          )}
          {!readOnly && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={addSet}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Source Goal Set
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Source Combobox ───────────────────────────────────────────────────

function SourceCombobox({
  allSources,
  selectedSources,
  onSelect,
}: {
  allSources: string[];
  selectedSources: string[];
  onSelect: (source: string) => void;
}) {
  const [search, setSearch] = useState("");

  const available = allSources.filter(
    (s) => !selectedSources.includes(s) && s.toLowerCase().includes(search.toLowerCase()),
  );

  const trimmed = search.trim();
  const isCustom =
    trimmed.length > 0 &&
    !allSources.some((s) => s.toLowerCase() === trimmed.toLowerCase()) &&
    !selectedSources.some((s) => s.toLowerCase() === trimmed.toLowerCase());

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search or type a source..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {available.length === 0 && !isCustom && <CommandEmpty>No source found.</CommandEmpty>}
        {isCustom && (
          <CommandItem
            value={trimmed}
            onSelect={() => {
              onSelect(trimmed);
              setSearch("");
            }}
          >
            <Plus className="h-3 w-3 mr-1.5 text-muted-foreground" />
            Add "{trimmed}"
          </CommandItem>
        )}
        {available.map((source) => (
          <CommandItem
            key={source}
            value={source}
            onSelect={() => {
              onSelect(source);
              setSearch("");
            }}
          >
            {source}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}

// ── Source Goal Set Card ──────────────────────────────────────────────

function SourceGoalSetCard({
  ownerId,
  set,
  allSources,
  onDelete,
  onUpdate,
  readOnly = false,
}: {
  ownerId: string;
  set: SourceGoalSet;
  allSources: string[];
  onDelete: () => void;
  onUpdate: (updates: Partial<SourceGoalSet>) => void;
  readOnly?: boolean;
}) {
  const [name, setName] = useState(set.name);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedNameRef = useRef(set.name);
  const [goals, setGoals] = useState<GoalRow[]>(
    set.targets.map((t) => ({ metric: t.metric, value: String(t.targetValue) })),
  );
  const [newMetricKey, setNewMetricKey] = useState<string | null>(null);
  const lastSavedTargetRef = useRef<Record<string, string>>(
    Object.fromEntries(set.targets.map((t) => [t.metric, String(t.targetValue)])),
  );
  const [sourcesOpen, setSourcesOpen] = useState(false);

  async function saveName() {
    if (name === lastSavedNameRef.current) return;
    const res = await fetch(`/api/owners/${ownerId}/source-goal-sets/${set.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      lastSavedNameRef.current = name;
      onUpdate({ name });
      flashSaved("name");
    }
  }

  async function toggleSource(source: string) {
    const current = set.sources;
    const updated = current.includes(source)
      ? current.filter((s) => s !== source)
      : [...current, source];

    const res = await fetch(`/api/owners/${ownerId}/source-goal-sets/${set.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sources: updated }),
    });
    if (res.ok) {
      onUpdate({ sources: updated });
      flashSaved("sources");
    }
  }

  const usedMetrics = new Set(goals.map((g) => g.metric));
  const availableToAdd = AVAILABLE_METRICS.filter((m) => !usedMetrics.has(m.key));
  const sortedGoals = [...goals].sort((a, b) => {
    if (a.metric === newMetricKey) return 1;
    if (b.metric === newMetricKey) return -1;
    return (getMetricDef(a.metric)?.label ?? "").localeCompare(getMetricDef(b.metric)?.label ?? "");
  });

  async function saveMetric(metric: string, value: string) {
    if (value === (lastSavedTargetRef.current[metric] ?? "")) return;

    const res = await fetch(`/api/owners/${ownerId}/source-goal-sets/${set.id}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        targets: [{ metric, targetValue: value === "" ? null : Number(value) }],
      }),
    });
    if (res.ok) {
      lastSavedTargetRef.current[metric] = value;
      if (newMetricKey === metric) setNewMetricKey(null);
      flashSaved(metric);
    }
  }

  function addGoal(metricKey: string) {
    setGoals((prev) => [...prev, { metric: metricKey, value: "" }]);
    setNewMetricKey(metricKey);
  }

  function removeGoal(metric: string) {
    setGoals((prev) => prev.filter((g) => g.metric !== metric));
    fetch(`/api/owners/${ownerId}/source-goal-sets/${set.id}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targets: [{ metric, targetValue: null }] }),
    });
    delete lastSavedTargetRef.current[metric];
  }

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-xs">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            disabled={readOnly}
            className={cn(
              "font-semibold transition-[border-color,box-shadow] duration-300",
              savedField === "name" && SAVED_BORDER,
            )}
          />
        </div>
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Sources multi-select */}
      <div className="space-y-1.5">
        <Label className="text-sm">Sources</Label>
        <div
          className={cn(
            "flex flex-wrap gap-1.5 transition-[border-color,box-shadow] duration-300",
            savedField === "sources" && SAVED_BORDER,
            "rounded-md border p-2 min-h-[36px]",
          )}
        >
          {set.sources.map((s) => (
            <Badge key={s} variant="secondary" className="gap-1">
              {s}
              {!readOnly && (
                <button
                  type="button"
                  className="ml-0.5 hover:text-destructive"
                  onClick={() => toggleSource(s)}
                >
                  ×
                </button>
              )}
            </Badge>
          ))}
          {!readOnly && (
            <Popover open={sourcesOpen} onOpenChange={setSourcesOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-0" align="start">
                <SourceCombobox
                  allSources={allSources}
                  selectedSources={set.sources}
                  onSelect={(source) => {
                    toggleSource(source);
                    setSourcesOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Metric targets */}
      <div className="space-y-3">
        {sortedGoals.map((goal) => (
          <GoalRowInput
            key={goal.metric}
            goal={goal}
            autoFocus={goal.metric === newMetricKey}
            onChangeValue={(v) =>
              setGoals((prev) =>
                prev.map((g) => (g.metric === goal.metric ? { ...g, value: v } : g)),
              )
            }
            onBlur={() => {
              const g = goals.find((g) => g.metric === goal.metric);
              if (g) saveMetric(goal.metric, g.value);
            }}
            onRemove={readOnly ? undefined : () => removeGoal(goal.metric)}
            disabled={readOnly}
            savedField={savedField}
          />
        ))}
      </div>

      {!readOnly && <AddGoalSelect availableMetrics={availableToAdd} onAdd={addGoal} />}
    </div>
  );
}
